import { Link } from "react-router";
import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import Header from "@components/Header/Header";
import { useBreadCrumbState } from "@components/Header/helpers";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useTrimmedBreakingNews } from "@hooks/useTrimmedBreakingNews";
import BreakingNews from "@routes/Section/components/BreakingNews/BreakingNews";
import { WealthPageData } from "@routes/Section/layouts/Wealth/utils/types";
import { sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";

import WealthRealEstate from "./components/RealEstate/WealthRealEstate";
import WealthThirdRow from "./components/ThirdRow/WealthThirdRow";
import TodaysMarketData from "./components/TodaysMarketData";
import WealthTopStories from "./components/TopStories/WealthTopStories";
import WealthWatches from "./components/Watches/WealthWatches";

const WealthPageContent = ({
  topStories,
  property,
  invest,
  advisor,
  asset,
  watches,
  wealthAttachment,
  seoFooterData,
}: WealthPageData): React.ReactElement => {
  const { breadcrumbsRef, isShowBreadCrumb } = useBreadCrumbState();
  const breakingNews = useTrimmedBreakingNews();

  return (
    <div className="w-full" data-testid="wealth-page-content-component">
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header
        displayBreadcrumb={sectionNavigationItems["wealth"] && isShowBreadCrumb}
        parentCategory={"wealth"}
        categoryDataMapping={sectionNavigationItems}
      />

      <PulseEntry />

      <Container>
        <div>
          <h1 className="pb-3 pt-6 font-playfair text-8xl font-semibold uppercase text-gray-850">
            Wealth
          </h1>

          <div ref={breadcrumbsRef}>
            <Breadcrumb
              className="m-auto"
              parentCategory={"wealth"}
              categoryDataMapping={sectionNavigationItems}
              extraBreadcrumbItems={
                <div
                  className={cn(
                    "relative box-border flex shrink-0 items-center border-r border-gray-175 pr-3 font-poppins text-sm font-bold tracking-tightest text-gray-850 hover:opacity-80"
                  )}
                >
                  <Link
                    to={`${RouteFactory.wealth}?ref=listing-menubar`}
                    reloadDocument
                  >
                    Top stories
                  </Link>
                </div>
              }
            />
          </div>
        </div>

        <div className="mb-12 mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <WealthTopStories
            cardProps={topStories}
            className="mb-3 lg:col-span-2 lg:mb-0"
          />

          <div className="space-y-6">
            <Ad
              adType="imu1"
              className="flex flex-col items-center justify-center"
            />

            {wealthAttachment ? (
              <TodaysMarketData wealthAttachment={wealthAttachment} />
            ) : null}

            <BreakingNews data={breakingNews} rootClassName="mx-auto" />
          </div>
        </div>

        <>
          {property ? (
            <WealthRealEstate cardProps={property} className="mb-12" />
          ) : null}
        </>

        <>
          {invest && advisor && asset ? (
            <WealthThirdRow
              cardProps={[...invest, ...advisor, ...asset]}
              className="mb-12"
            />
          ) : null}
        </>

        <>
          {watches ? (
            <WealthWatches cardProps={watches} className="mb-12" />
          ) : null}
        </>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />
      <Ad adType="prestitial" />
    </div>
  );
};

export default WealthPageContent;
